import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "../ui/button";

/**
 * Shared full-screen modal shell used by every workspace "yeni/ düzenle" flow.
 * - Does NOT close on outside click (per user request). Only the X button or
 *   the explicit "Vazgeç" button closes it, after a confirm when dirty=true.
 * - Max-h with internal scroll so the toolbar of the embedded RichEditor stays
 *   fully usable on phones (Samsung Fold ~344px works).
 * - Children are the form body (title row, editor, side fields, etc.).
 */
export default function WorkspaceEditorModal({
  open,
  onClose,
  title,
  icon: Icon,
  size = "lg",      // sm | md | lg | xl
  busy = false,
  dirty = false,
  onSubmit,
  submitLabel = "Kaydet",
  secondary,        // optional: { label, onClick, variant }
  children,
  testid = "ws-modal",
  accent = "indigo",
}) {
  // Lock background scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") askClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dirty]);

  if (!open) return null;

  const askClose = () => {
    if (dirty && !window.confirm("Kaydedilmemiş değişiklikler kaybolacak. Çıkmak istiyor musunuz?")) return;
    onClose();
  };

  const widths = {
    sm: "max-w-md",
    md: "max-w-2xl",
    lg: "max-w-4xl",
    xl: "max-w-6xl",
  };
  const accents = {
    indigo:  "from-indigo-600 to-violet-600",
    rose:    "from-rose-600 to-pink-600",
    amber:   "from-amber-600 to-orange-600",
    emerald: "from-emerald-600 to-teal-600",
    sky:     "from-sky-600 to-blue-600",
  };

  const node = (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm p-0 sm:p-3 md:p-4 flex items-stretch sm:items-center justify-center" data-testid={testid}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${widths[size]} h-full sm:h-auto sm:max-h-[95vh] bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden`}
      >
        {/* Gradient header */}
        <div className={`px-3 sm:px-5 py-3 sm:py-3.5 flex items-center gap-2 text-white bg-gradient-to-r ${accents[accent] || accents.indigo} shrink-0`}>
          {Icon && <Icon size={18} className="shrink-0" />}
          <h2 className="font-serif text-base sm:text-lg font-bold truncate flex-1" data-testid={`${testid}-title`}>{title}</h2>
          <button
            type="button"
            onClick={askClose}
            className="p-1.5 rounded-lg hover:bg-white/20 shrink-0"
            aria-label="Kapat"
            data-testid={`${testid}-close`}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 p-3 sm:p-5 bg-slate-50/60">
          {children}
        </div>

        {/* Footer actions */}
        <div className="px-3 sm:px-5 py-3 border-t border-stone-200 bg-white flex items-center flex-wrap gap-2 shrink-0">
          <Button type="button" variant="outline" size="sm" onClick={askClose} data-testid={`${testid}-cancel`}>Vazgeç</Button>
          {secondary && (
            <Button
              type="button"
              size="sm"
              variant={secondary.variant || "outline"}
              onClick={secondary.onClick}
              disabled={busy || secondary.disabled}
              data-testid={`${testid}-secondary`}
            >
              {secondary.icon && <secondary.icon size={13} className="mr-1" />}
              {secondary.label}
            </Button>
          )}
          <span className="flex-1" />
          <Button type="button" size="sm" onClick={onSubmit} disabled={busy} data-testid={`${testid}-submit`}>
            {busy ? "Kaydediliyor..." : submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(node, document.body) : null;
}
